import {
  ChangeDetectorRef,
  Component,
  computed,
  ElementRef,
  inject,
  OnDestroy,
  OnInit,
  signal,
  ViewChild,
} from '@angular/core';
import { MatMenuTrigger } from '@angular/material/menu';

import { Title } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { TranslocoService, translate } from '@jsverse/transloco';

import { capitalize } from 'humanize-plus';

import { Subject, Subscription, takeUntil } from 'rxjs';

import { v4 as UUIDv4 } from 'uuid';

import { SimpleDialogButtonColor } from 'app/components/simple-dialog.component';
import { HasTitle } from 'app/has-title';
import { Filter } from 'app/modules/collection/services/filter.service';
import { DateMarker } from 'app/modules/widget/components/widget-chart/widget-chart.component';
import {
  WidgetFilterDialogComponent,
  WidgetFilterDialogOptions,
} from 'app/modules/widget/components/widget-filter/widget-filter.component';
import { AnalyticsService, TimerFn } from 'app/providers/analytics.service';
import { AuthService } from 'app/providers/bridge-api/auth.service';
import {
  ActionPlanSidePaneData,
  Comparison,
  Dashboard,
  Widget,
  WidgetGroup,
} from 'app/providers/bridge-api/dashboard.service';
import {
  SavedViewBackendModel,
  SavedViewService,
} from 'app/providers/bridge-api/saved-view.service';
import { ActionPlan } from 'app/providers/storage-api/actionplan.service';
import { AlertService } from 'app/services/alert.service';
import { DialogService } from 'app/services/dialog.service';
import { LocalStorageKey, LocalStorageService } from 'app/services/local-storage.service';
import { LoggerService } from 'app/services/logger.service';
import {
  EnableClassTelemetry,
  EnableComponentTelemetry,
  injectComponentTelemetryService,
} from 'app/services/otel.service';
import { Utils } from 'app/utils/utils';

import {
  ActivityBarInteraction,
  ActivityBarPillTypeEnum,
  EntityChange,
} from '../../../../components/dashboard-pill/dashboard-pill.component';
import { WidgetDataSource } from '../../../../models';
import { Project } from '../../../../providers/storage-api/project.service';
import { DashboardViewComponent } from '../dashboard-view/dashboard-view.component';
import {
  ActionPlanSidePaneItemComponent,
  AddToComparisonActionData,
  AddToWidgetGroupActionData,
} from '../../components/action-plan-side-pane-item/action-plan-side-pane-item.component';
import { ComparisonSidePaneItemComponent } from '../../components/comparison-side-pane-item/comparison-side-pane-item.component';
import { SavedViewSidePaneItemComponent } from '../../components/saved-view-side-pane-item/saved-view-side-pane-item.component';
import { WidgetGroupCreateDialogComponent } from '../../components/widget-group-create/widget-group-create.component';
import { WidgetGroupDataSourceSelectorDialogComponent } from '../../components/widget-group-datasource-selector/widget-group-datasource-selector.component';
import { WidgetGroupSidePaneItemComponent } from '../../components/widget-group-side-pane-item/widget-group-side-pane-item.component';
import { SavedView } from '../../models/saved-view.model';
import { ActionItemCacheService } from '../../services/action-item-cache.service';
import { ActionPlanCacheService } from '../../services/action-plan-cache.service';
import { ActionPlanSidePaneService } from '../../services/action-plan-side-pane.service';
import { ComparisonHandlerService } from '../../services/comparison-handler-service';
import { SavedViewPropagationService } from '../../services/saved-view-propagation.service';
import { DashboardStore } from '../../stores/dashboard.store';
import { DatasourceManagerStore } from '../../stores/datasource-manager.store';
import { SelectModeStore } from '../../stores/select-mode.store';
import { WidgetGroupStore } from '../../stores/widget-group.store';
import {
  DashboardPageChangeData,
  WidgetGroupSidePanelData,
} from './../dashboard-view/dashboard-view.component';

export interface WidgetSelectMode {
  mode:
    | 'filters'
    | 'edit'
    | 'widgetGroup'
    | 'widgetGroupBulkEdit'
    | 'comparison'
    | 'analyticItem'
    | 'goal'
    | 'dateMarker'
    | 'weight'
    | 'addFilter';
  data: any;
  selected: Widget[];
  isActivityBarSelectAction?: boolean;
}

export interface ComparisonApplyConfig {
  isActivityBarSelectAction: boolean;
}

@EnableClassTelemetry()
@Component({
  selector: 'app-dashboard-details',
  templateUrl: './dashboard-details.component.html',
  styleUrls: ['./dashboard-details.component.scss'],
  providers: [
    ActionPlanSidePaneService,
    ActionPlanCacheService,
    ActionItemCacheService,
    WidgetGroupStore,
    SelectModeStore,
    DatasourceManagerStore,
    DashboardStore,
  ],
  standalone: false,
})
export class DashboardDetailsComponent extends HasTitle implements OnInit, OnDestroy {
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();
  @ViewChild(MatMenuTrigger, { static: true }) sortMenuTrigger: MatMenuTrigger;
  @ViewChild('dashboardView') dashboardViewComponent: DashboardViewComponent;

  readonly widgetGroupStore = inject(WidgetGroupStore);
  readonly selectModeStore = inject(SelectModeStore);
  readonly datasourceManagerStore = inject(DatasourceManagerStore);
  readonly dashboardStore = inject(DashboardStore);

  isLoading = true;
  isSaving = new Subject<boolean>();

  MAX_FILTER_GROUP_COUNT = 100;

  sortTypes = Object.freeze({
    DATE: 1,
    NAME: 2,
  });
  currentSort = this.sortTypes.DATE;
  reverseSort = true;
  widgetGroupSort = '2';
  comparisonList: Comparison[] = [];
  selectedComparison: Comparison;

  WidgetGroupSidePaneItemComponent = WidgetGroupSidePaneItemComponent;
  ComparisonSidePaneItemComponent = ComparisonSidePaneItemComponent;
  ActionPlanSidePaneItemComponent = ActionPlanSidePaneItemComponent;
  SavedViewSidePaneItemComponent = SavedViewSidePaneItemComponent;

  projectId: string;
  isExecutive: boolean;
  canEditWidgetGroup: boolean;
  reportTiming: TimerFn;
  //Side panel
  isPanelOpen;
  isCreating = false;
  isEditing = false;
  isDeleting = false;
  isDashboardUnsaved = false;
  unsavedPagesTooltip: string;
  previousPeriodLabel: string;
  currentPeriodLabel: string;
  currentDashboardPageId = signal<number>(undefined);
  widgetGroupList = computed(() => this.widgetGroupStore.getByPage(this.currentDashboardPageId()));
  showCreateComparison;

  isAddActionPlanDialogOpen = false;
  actionPlanSidePaneDataList: ActionPlanSidePaneData[] = [];
  isWidgetGroupChooserOpen = false;
  isComparisonChooserOpen = false;
  addToComparisonActionData: AddToComparisonActionData;
  addToWidgetGroupActionData: AddToWidgetGroupActionData;
  selectedActionPlanIds: string[];
  widgetGroupsToChoose: WidgetGroup[];
  comparisonsToChoose: Comparison[];
  actionPlans: ActionPlan[] = [];
  isActionPlansAvailable = true;

  savedViews: SavedView[] = [];
  activeSavedViewId: string;
  private dashboardSavedViewsDashboardId: string;
  private activeSavedViewCache?: SavedView;

  isSavedViewDialogOpen = false;
  savedViewDialogExistingNames: string[] = [];
  savedViewDialogInitialName?: string;
  savedViewDialogTitle?: string;
  private savedViewDialogMode: 'create' | 'rename' = 'create';
  private savedViewToRename?: SavedView;

  unsub$ = new Subject<void>();
  translationSubscription: Subscription;
  project: Project;

  constructor(
    protected titleService: Title,
    private changeDetectorRef: ChangeDetectorRef,
    private route: ActivatedRoute,
    private dialogService: DialogService,
    private authService: AuthService,
    private googleAnalyticsService: AnalyticsService,
    private localStorageService: LocalStorageService,
    private alertService: AlertService,
    private comparisonService: ComparisonHandlerService,
    private actionPlanSidePaneService: ActionPlanSidePaneService,
    private actionPlanCacheService: ActionPlanCacheService,
    private element: ElementRef,
    private translocoService: TranslocoService,
    private savedViewService: SavedViewService,
    private savedViewPropagationService: SavedViewPropagationService,
    private logger: LoggerService
  ) {
    super(titleService);

    this.reportTiming = this.googleAnalyticsService.createTimer('Widget Groups');

    this.isPanelOpen =
      this.localStorageService.getItem(LocalStorageKey.WIDGET_GROUP_PANEL_OPEN) ?? true;
  }

  async ngOnInit() {
    this.isLoading = true;
    this.translationSubscription = this.translocoService
      .selectTranslateObject('dashboard-details-component')
      .subscribe((translatedValue) => {
        this.setTitle(translatedValue['title']);
      });

    this.comparisonService.comparisonListChanged$
      .pipe(takeUntil(this.unsub$))
      .subscribe((res) => (this.comparisonList = res));

    this.actionPlanSidePaneService.sidePaneDataListChanged$
      .pipe(takeUntil(this.unsub$))
      .subscribe((dataList) => {
        this.actionPlanSidePaneDataList = dataList;
        this.setActionPlansAvailable();
      });

    this.actionPlanCacheService.delete$.pipe(takeUntil(this.unsub$)).subscribe((id) => {
      this.actionPlanDeleted(id);
      this.setActionPlansAvailable();
    });

    this.actionPlanCacheService.add$.pipe(takeUntil(this.unsub$)).subscribe((a) => {
      this.actionPlans.push(a);
      this.setActionPlansAvailable();
    });

    const params = this.route.snapshot.paramMap;
    this.projectId = params.get('projectId');

    try {
      await this.actionPlanCacheService.waitUntilLoaded;
      this.isExecutive = this.authService.isExecutive;
      this.canEditWidgetGroup = !this.authService.isReadOnly;

      this.actionPlans = this.actionPlanCacheService.getAll();
      this.setActionPlansAvailable();

      this.reportTiming('document_filter_groups_loaded');
      this.isLoading = false;
    } catch (error) {
      this.logger.error('widget-group-list ngOnInit error', { error });
      this.isLoading = false;
    }
  }

  ngOnDestroy(): void {
    this.unsub$.next();
    this.unsub$.complete();
    this.translationSubscription?.unsubscribe();
  }

  public onSetWidgetGroupPanel(widgetGroupSidePanelData: WidgetGroupSidePanelData) {
    this.element.nativeElement.querySelector(
      'app-lex-xi-side-panel button.kxi-side-panel-toggle-button'
    ).disabled = widgetGroupSidePanelData.dashboardIsLocked;

    if (widgetGroupSidePanelData.sidePanelIsOpened !== undefined) {
      this.isPanelOpen = widgetGroupSidePanelData.sidePanelIsOpened;
    }
  }

  public onPanelStateChange(isOpen: boolean) {
    this.localStorageService.setItem(LocalStorageKey.WIDGET_GROUP_PANEL_OPEN, isOpen);

    this.onPanelStateChanged();
  }

  public get isReadOnly() {
    return this.authService.isReadOnly;
  }

  public createWidgetGroup(): void {
    if (!this.isCreating) {
      this.isCreating = true;
      this.dialogService
        .showDialog<WidgetGroupCreateDialogComponent>(
          WidgetGroupCreateDialogComponent,
          {
            widgetGroups: this.widgetGroupList(),
            project: this.project,
          },
          {},
          false
        )
        .afterClosed()
        .subscribe(async (response) => {
          if (response) {
            this.selectModeStore.setSelectMode('widgetGroup', {
              name: response.name,
              widgetGroupColor: response.widgetGroupColor,
            });
          } else {
            this.isCreating = false;
          }
        });
    }
  }

  onWidgetGroupAction($event, activityBarEvent?: ComparisonApplyConfig) {
    switch ($event.action) {
      case 'delete':
        this.onDeleteWidgetGroup($event.item);
        break;
      case 'edit':
        this.onEditWidgetGroup($event.item);
        break;
      case 'bulkEdit':
        this.onBulkEditWidgetGroupMembers($event.item, activityBarEvent);
        break;
      case 'clone':
        this.onCloneWidgetGroup($event.item, $event.data?.pageId);
        break;
      case 'rename':
        this.onRenameWidgetGroup($event.item);
        break;
      case 'filterMove':
      case 'filterDelete':
      case 'filterChange':
        let filterToastLabel: string | null = null;
        if ($event.action === 'filterDelete') {
          filterToastLabel = translate('dashboard-details-component.filter-removed');
        } else if ($event.action === 'filterChange') {
          filterToastLabel = translate('dashboard-details-component.filter-updated');
        }
        this.dashboardViewComponent.onUpdateWidgetGroup(
          $event.item,
          undefined,
          filterToastLabel,
          true,
          !$event.item.isImpact
        );
        break;
      case 'changeWidgetGroup':
        const changeWidgetGroupWidgetComponent = this.dashboardViewComponent.elementFor(
          $event?.widget?.id
        );
        if (changeWidgetGroupWidgetComponent) {
          if (changeWidgetGroupWidgetComponent.isWidgetGroupParent) {
            changeWidgetGroupWidgetComponent.adjustingFilterGroupParent = true;
          }
          changeWidgetGroupWidgetComponent.onWidgetWidgetGroupModalClosed({
            addToGroup: $event?.item?.new?.id,
            removeFromGroup: $event?.item?.old?.id,
          });
        }
        break;
      case 'remove':
        const removeWidgetComponent = this.dashboardViewComponent.elementFor($event?.widget?.id);
        if (removeWidgetComponent) {
          removeWidgetComponent.onRemoveFromWidgetGroup();
        }
        break;
      case 'demote':
        const demoteWidgetComponent = this.dashboardViewComponent.elementFor($event?.widget?.id);
        if (demoteWidgetComponent) {
          demoteWidgetComponent.onDemoteFromWidgetGroup();
        }
        break;
    }
  }

  public async onDeleteWidgetGroup(widgetGroup): Promise<void> {
    if (!this.isDeleting) {
      this.isDeleting = true;
      this.dialogService
        .showWarningPopup({
          title: translate('dashboard-details-component.delete-title'),
          content: translate('dashboard-details-component.delete-contents'),
          confirmTitle: translate('dashboard-details-component.delete-confirm'),
          cancelTitle: translate('dashboard-details-component.delete-cancel'),
          confirmColor: SimpleDialogButtonColor.red,
        })
        .afterClosed()
        .subscribe(async (result) => {
          if (result) {
            try {
              this.dashboardViewComponent.onDeleteWidgetGroup(widgetGroup);
            } finally {
              this.isDeleting = false;
            }
          } else {
            this.isDeleting = false;
          }
        });
    }
  }

  async onEditWidgetGroup(widgetGroup) {
    if (this.isEditing || !this.datasourceManagerStore.hasData() || !this.projectId) {
      return;
    }

    this.isEditing = true;
    let dataSourceSelected;
    this.dialogService
      .showDialog<WidgetGroupDataSourceSelectorDialogComponent>(
        WidgetGroupDataSourceSelectorDialogComponent,
        {
          previousDataSourceId: widgetGroup.lastUsedDataSource,
          dataSources: this.datasourceManagerStore.getDatasources(),
        },
        {},
        false
      )
      .afterClosed()
      .subscribe(async (response) => {
        if (response) {
          const widget = {
            id: 'dummy',
            component: '',
            title: 'dummy',
            payload: {
              filters: response.dataSource.filters,
              projectId: this.projectId,
            },
            x: 0,
            y: 0,
            cols: 2,
            rows: 2,
            dataSourceId: response.dataSource.id,
            properties: {
              is_locked: false,
              as_data_table: false,
              as_document_list: false,
            },
          };
          dataSourceSelected = response.dataSource.id;
          this.dialogService
            .showDialog<WidgetFilterDialogComponent, WidgetFilterDialogOptions>(
              WidgetFilterDialogComponent,
              {
                widget,
                projectId: this.projectId,
                analyticItem: '',
                sentimentScale: this.dashboardViewComponent.dashboard.payload.sentimentScale,
                filters: widgetGroup.filters,
                widgetDataSource: response.dataSource,
                widgetGroup,
                forWidgetGroup: true,
              },
              {},
              false
            )
            .afterClosed()
            .subscribe(async (result) => {
              if (result) {
                widgetGroup.filters = JSON.parse(JSON.stringify(result.filters));
                widgetGroup.lastUsedDataSource = dataSourceSelected;
                const dataSource = this.datasourceManagerStore.getById(
                  widgetGroup.lastUsedDataSource
                );
                widgetGroup.lastUsedDataSourceDisplayName = dataSource.displayName;
                this.dashboardViewComponent.onUpdateWidgetGroup(
                  widgetGroup,
                  undefined,
                  undefined,
                  true
                );
                this.isEditing = false;
              } else {
                this.isEditing = false;
              }
            });
        } else {
          this.isEditing = false;
        }
      });
  }

  onBulkEditWidgetGroupMembers(widgetGroup, activityBarEvent?: ComparisonApplyConfig) {
    const pageWidgets = this.dashboardViewComponent.currentPageWidgets;
    //Find which page widgets are also in widget group
    const selectedPageWidgets = pageWidgets.filter((w) => widgetGroup.widgets.includes(w.id));
    const selected = [...selectedPageWidgets];
    //If the widget group has a parent widget, find which widget it is
    if (widgetGroup.parentWidget) {
      const foundParentWidget = pageWidgets.find((item) => item.id === widgetGroup.parentWidget);
      if (foundParentWidget) {
        selected.push(foundParentWidget);
      }
    }
    this.selectModeStore.setSelectMode(
      'widgetGroupBulkEdit',
      { widgetGroup },
      selected,
      activityBarEvent?.isActivityBarSelectAction ?? false
    );
  }

  onCloneWidgetGroup(widgetGroup, pageId = this.currentDashboardPageId()) {
    const clone = this.widgetGroupStore.cloneWidgetGroup(widgetGroup);

    clone.page = pageId;
    clone.widgets = [];
    clone.title = translate('dashboard-details-component.copy', {
      cloneTitle: clone.title,
    });
    delete clone.parentWidget;

    const groupsOnTargetPage = this.widgetGroupStore.getByPage(pageId);

    clone.title = this.generateName(
      groupsOnTargetPage.map((wg) => wg.title),
      clone.title
    );

    this.alertService.showToast(
      translate('dashboard-details-component.copy-create', {
        cloneTitle: clone.title,
        suffix: this.getToastSuffix(pageId),
      })
    );
    this.dashboardStore.markDirty();

    this.dashboardViewComponent.dashboard.widgetGroups[clone.id] = clone;
    this.widgetGroupStore.registerWidgetGroup(clone);
    this.onRefreshWidgetGroups(true);
  }

  onRenameWidgetGroup(widgetGroup: WidgetGroup) {
    this.dialogService
      .showDialog(
        WidgetGroupCreateDialogComponent,
        {
          name: widgetGroup.title,
          widgetGroupColor: widgetGroup.color,
          widgetGroups: this.widgetGroupList().filter((d) => d.id !== widgetGroup.id),
          project: this.project,
        },
        {},
        false
      )
      .afterClosed()
      .subscribe((result) => {
        if (result) {
          const currentWidgetGroup = this.widgetGroupList().find(
            (group) => group.id === widgetGroup.id
          );
          if (currentWidgetGroup) {
            currentWidgetGroup.title = result.name;
            currentWidgetGroup.color = result.widgetGroupColor;
            this.widgetGroupStore.updateWidgetGroup(
              this.dashboardViewComponent.dashboard,
              currentWidgetGroup
            );
          }
          this.dashboardStore.markDirty();
        }
      });
  }

  onRefreshWidgetGroups(shouldRefresh: boolean) {
    this.isCreating = false;
    this.isDeleting = false;
    this.isEditing = false;
    if (shouldRefresh) {
      setTimeout(() => {
        this.changeDetectorRef.detach();
        this.changeDetectorRef.detectChanges();
        this.changeDetectorRef.detectChanges();
        this.changeDetectorRef.reattach();
      }, 50);
    }
  }

  createComparison() {
    this.showCreateComparison = true;
  }

  onCreateComparisonClosed(comparison?) {
    this.showCreateComparison = false;

    if (comparison) {
      this.dashboardViewComponent.dashboard.comparisons[comparison.id] = comparison;
      if (comparison.page === this.currentDashboardPageId()) {
        if (this.selectedComparison) {
          this.comparisonService.updateComparison(comparison);
        } else {
          this.comparisonService.addComparison(comparison);
        }
      }
    }
    this.dashboardStore.markDirty();
    this.selectedComparison = undefined;
  }

  onComparisonAction($event, comparisonApplyConfig?: ComparisonApplyConfig) {
    switch ($event.action) {
      case 'edit':
        this.onEditComparison($event.item);
        break;
      case 'apply':
        this.onApplyComparison($event.item, comparisonApplyConfig);
        break;
      case 'clone':
        this.onCloneComparison($event.item, $event.data?.pageId);
        break;
      case 'delete':
        this.onDeleteComparison($event.item);
        break;
    }
  }

  onEditComparison(comparison) {
    this.selectedComparison = comparison;

    this.createComparison();
  }

  onApplyComparison(comparison, comparisonApplyConfig?: ComparisonApplyConfig) {
    const dashboardWidgets = this.dashboardViewComponent.dashboard.widgets;
    const selected = comparison.widgets
      .map((widgetId) => dashboardWidgets.find((item) => item.id === widgetId))
      .filter(Boolean);
    this.selectModeStore.setSelectMode(
      'comparison',
      { comparison },
      selected,
      comparisonApplyConfig?.isActivityBarSelectAction ?? false
    );
  }

  onCloneComparison(comparison, pageId = this.currentDashboardPageId()) {
    const clone = Utils.deepCloneWithNulls(comparison);

    clone.id = UUIDv4();
    clone.widgets = [];
    clone.page = pageId;
    clone.title = translate('dashboard-details-component.copy', {
      cloneTitle: clone.title,
    });
    const comparisonsOnTargetPage = Object.values(
      this.dashboardViewComponent.dashboard.comparisons
    ).filter((c) => c.page === pageId);

    clone.title = this.generateName(
      comparisonsOnTargetPage.map((c) => c.title),
      clone.title
    );

    this.onCreateComparisonClosed(clone);

    this.alertService.showToast(
      translate('dashboard-details-component.copy-create', {
        cloneTitle: clone.title,
        suffix: this.getToastSuffix(pageId),
      })
    );
  }

  onDeleteComparison(comparison) {
    this.dialogService
      .showWarningPopup({
        title: translate('dashboard-details-component.delete-comparison-title'),
        content: translate('dashboard-details-component.delete-comparison-contents'),
        confirmTitle: translate('dashboard-details-component.delete-confirm'),
        cancelTitle: translate('dashboard-details-component.delete-cancel'),
        confirmColor: SimpleDialogButtonColor.red,
      })
      .afterClosed()
      .subscribe((result) => {
        if (result) {
          delete this.dashboardViewComponent.dashboard.comparisons[comparison.id];

          this.comparisonService.deleteComparison(comparison);
          this.dashboardStore.markDirty();

          this.alertService.showToast(
            translate('dashboard-details-component.delete-comparison-success', {
              title: comparison.title,
            })
          );
        }
      });
  }

  onRefreshComparisons() {
    if (this.dashboardViewComponent?.dashboard) {
      this.comparisonList = Object.values(this.dashboardViewComponent.dashboard.comparisons);
    }
  }

  public revertDashboard(): void {
    this.dashboardViewComponent.revertDashboard();
  }

  public cancelNewDashboard() {
    this.dashboardViewComponent.cancelNewDashboard();
  }

  public saveDashboard(): void {
    this.isSaving.next(true);
    this.dashboardViewComponent.saveDashboard().finally(() => this.isSaving.next(false));
  }

  onPanelStateChanged() {
    setTimeout(() => window.dispatchEvent(new Event('resize')));
  }

  getWidgetCount(filterGroup: WidgetGroup): number {
    if (!filterGroup.parentWidget || filterGroup.widgets.includes(filterGroup.parentWidget)) {
      return filterGroup.widgets.length;
    }

    return filterGroup.widgets.length + 1;
  }

  getFilterCount(filterGroup: WidgetGroup): number {
    const count = filterGroup.parameters.scoreMetric ? 3 : 0;

    return (filterGroup.andFilters?.length || 0) + count;
  }

  onWidgetsCountChanges(dashboard: Dashboard) {
    if (dashboard.payload.type === 'predictive') {
      const impactWidgetGroup = this.widgetGroupList().find((filter) => filter.isImpact);
      if (impactWidgetGroup) {
        impactWidgetGroup.editable = dashboard.widgets.length !== 3;
      }
    }
  }

  onDashboardPageChanged($event: number) {
    this.currentDashboardPageId.set($event);
  }

  onUnsavedPagesChanged($event: DashboardPageChangeData) {
    const tooltipArray = [
      ...[...$event.updated].map((p) => p.title),
      ...[...$event.added].map((p) => p.title + ' (Added)'),
      ...[...$event.deleted].map((p) => p.title + ' (Deleted)'),
    ];

    if ($event.reordered) {
      tooltipArray.push('Pages reordered');
    }

    this.unsavedPagesTooltip = tooltipArray.join('\n');
  }

  onAddActionPlan(): void {
    this.selectedActionPlanIds = this.actionPlanSidePaneDataList.map((d) => d.id);

    this.isAddActionPlanDialogOpen = true;
  }

  onCloseAddActionPlanDialog(actionPlanIds: string[] | null): void {
    if (actionPlanIds) {
      const actionPlanSidePaneDataList = actionPlanIds.map((id) => ({
        id,
        page: this.currentDashboardPageId(),
      }));

      this.dashboardViewComponent.dashboard.payload.actionPlans.push(...actionPlanSidePaneDataList);

      this.actionPlanSidePaneService.add(actionPlanSidePaneDataList);

      this.alertService.showToast(translate('dashboard-details-component.action-plans-added'));
    }
    // Actionplans are not a feature as of date, adding it just in case futureproofing
    this.dashboardStore.markDirty();

    this.isAddActionPlanDialogOpen = false;
  }

  onActionPlanAction($event: { action: string; data?: any }): void {
    switch ($event.action) {
      case 'delete':
        this.deleteActionPlanSidePaneItem($event.data as ActionPlanSidePaneData);
        break;
      case 'create_wg':
        this.addWidgetGroup($event.data as WidgetGroup);
        break;
      case 'create_c':
        this.addComparison($event.data as Comparison);
        break;
      case 'add_wg':
        this.addToWidgetGroup($event.data as AddToWidgetGroupActionData);
        break;
      case 'add_c':
        this.addToComparison($event.data as AddToComparisonActionData);
        break;
      case 'set_item':
        this.setAnalyticItem($event.data as string);
        break;
      case 'set_goal':
        this.setGoal($event.data as number);
        break;
      case 'set_date_marker':
        this.setDateMarker($event.data as DateMarker);
        break;
      case 'set_weight':
        this.setWeight($event.data as string);
        break;
      case 'add_f':
        this.addFilter($event.data as Filter);
        break;
    }
  }

  private deleteActionPlanSidePaneItem(data: ActionPlanSidePaneData): void {
    this.dashboardViewComponent.dashboard.payload.actionPlans.splice(
      this.dashboardViewComponent.dashboard.payload.actionPlans.indexOf(data),
      1
    );

    this.actionPlanSidePaneService.remove(data);
    // Actionplans are not a feature as of date, but adding it just in case of futureproofing
    this.dashboardStore.markDirty();

    this.alertService.showToast(translate('dashboard-details-component.action-plan-removed'));
  }

  private addWidgetGroup(wg: WidgetGroup): void {
    const widgetGroups = this.widgetGroupStore.getByPage(this.currentDashboardPageId());

    wg.title = this.generateName(
      widgetGroups.map((g) => g.title),
      wg.title
    );

    this.alertService.showToast(
      translate('dashboard-details-component.widget-group-created', {
        name: wg.title,
      })
    );
    this.dashboardViewComponent.dashboard.widgetGroups[wg.id] = wg;
    this.widgetGroupStore.registerWidgetGroup(wg);
    this.dashboardStore.markDirty();
    this.onRefreshWidgetGroups(true);
  }

  private addComparison(comparison: Comparison): void {
    const comparisons = Object.values(this.dashboardViewComponent.dashboard.comparisons).filter(
      (c) => c.page === this.currentDashboardPageId()
    );

    comparison.title = this.generateName(
      comparisons.map((c) => c.title),
      comparison.title
    );

    this.alertService.showToast(
      translate('dashboard-details-component.comparison-created', {
        name: comparison.title,
      })
    );

    this.dashboardViewComponent.dashboard.comparisons[comparison.id] = comparison;
    this.comparisonService.addComparison(comparison);
    this.dashboardStore.markDirty();
  }

  private generateName(list: string[], name: string): string {
    const suffixRegex = /\((\d+)\)$/m;

    const generator = (n) => {
      const suffixMatch = n.match(suffixRegex);

      if (!suffixMatch) {
        return `${n} (1)`;
      }

      return n.replace(suffixRegex, `(${+suffixMatch[1] + 1})`);
    };

    while (list.some((i) => i === name)) {
      name = generator(name);
    }

    return name;
  }

  private getToastSuffix(page: number): string {
    let toastSuffix = '';

    if (page !== this.currentDashboardPageId()) {
      toastSuffix = `${translate('dashboard-details-component.on-page')} ${
        this.dashboardViewComponent.dashboard.payload.pages.find((p) => p.id === page).title
      }`;
    }

    return toastSuffix;
  }

  private addToWidgetGroup(actionData: AddToWidgetGroupActionData): void {
    this.addToWidgetGroupActionData = actionData;

    this.widgetGroupsToChoose = this.widgetGroupList().filter((wg) => !wg.isImpact);

    this.isWidgetGroupChooserOpen = true;
  }

  onCloseWidgetGroupChooser(widgetGroupId: string | null): void {
    if (widgetGroupId) {
      const widgetGroup = this.dashboardViewComponent.dashboard.widgetGroups[widgetGroupId];

      if (this.addToWidgetGroupActionData.isBreakdown) {
        widgetGroup.filters.metadata.and = [this.addToWidgetGroupActionData.filter];
      } else {
        widgetGroup.filters.metadata.and.push(this.addToWidgetGroupActionData.filter);
      }

      this.dashboardViewComponent.onUpdateWidgetGroup(
        widgetGroup,
        undefined,
        translate('dashboard-details-component.filter-added'),
        true
      );
    }

    this.isWidgetGroupChooserOpen = false;
  }

  private addToComparison(actionData: AddToComparisonActionData): void {
    this.addToComparisonActionData = actionData;

    this.comparisonsToChoose = this.comparisonList.filter((c) => c.type === actionData.type);

    this.isComparisonChooserOpen = true;
  }

  onCloseComparisonChooser(comparisonId: string | null): void {
    if (comparisonId) {
      const comparison = this.comparisonList.find((c) => c.id === comparisonId);

      const bucketName = this.generateName(
        comparison.filters.map((fs) => fs.label),
        capitalize(this.addToComparisonActionData.type)
      );

      comparison.filters.push({
        and: [this.addToComparisonActionData.filter],
        or: [],
        not: [],
        label: bucketName,
        id: UUIDv4(),
      });

      this.dashboardViewComponent.dashboard.comparisons[comparison.id] = comparison;
      this.comparisonService.updateComparison(comparison);
      this.dashboardStore.markDirty();

      this.alertService.showToast(translate('dashboard-details-component.filter-added'));
    }

    this.isComparisonChooserOpen = false;
  }

  private setAnalyticItem(columnId: string): void {
    this.selectModeStore.setSelectMode('analyticItem', { columnId });
  }

  private setGoal(goal: number): void {
    this.selectModeStore.setSelectMode('goal', { goal });
  }

  private setDateMarker(dateMarker: DateMarker): void {
    this.selectModeStore.setSelectMode('dateMarker', dateMarker);
  }

  private setWeight(weightField: string): void {
    this.selectModeStore.setSelectMode('weight', { weightField });
  }

  private addFilter(filter: Filter): void {
    this.selectModeStore.setSelectMode('addFilter', { filter });
  }

  private actionPlanDeleted(id: string): void {
    const actionPlan = this.dashboardViewComponent.dashboard.payload.actionPlans.find(
      (a) => a.id === id && a.page === this.currentDashboardPageId()
    );
    if (actionPlan) {
      this.deleteActionPlanSidePaneItem(actionPlan);
    }

    this.actionPlans.splice(
      this.actionPlans.findIndex((a) => a.plan_id === id),
      1
    );

    this.dashboardViewComponent.dashboard.payload.actionPlans =
      this.dashboardViewComponent.dashboard.payload.actionPlans.filter((a) => a.id !== id);
  }

  private setActionPlansAvailable(): void {
    if (
      this.actionPlans.every((a) => this.actionPlanSidePaneDataList.find((d) => d.id === a.plan_id))
    ) {
      this.isActionPlansAvailable = false;
    } else {
      this.isActionPlansAvailable = true;
    }
  }

  get activeSavedView(): SavedView | undefined {
    return this.activeSavedViewCache;
  }

  private async getDashboardSnapshotForSavedView(): Promise<Dashboard | null> {
    if (!this.dashboardViewComponent?.dashboard) {
      return null;
    }

    if (this.isReadOnly) {
      return this.dashboardViewComponent.dashboard;
    }

    try {
      return await this.dashboardViewComponent.saveDashboard({ silent: true });
    } catch (error) {
      this.alertService.showErrorToast(
        translate(
          'dashboard-details-component.saved-views.error-save-dashboard-before-saving-view'
        ),
        error
      );
      return null;
    }
  }

  onAddSavedView(): void {
    const activeView = this.activeSavedView;

    if (activeView?.hasUnsavedChanges) {
      this.dialogService
        .showSimpleDialog({
          title: translate('dashboard-details-component.saved-views-unsaved-dialog.title'),
          content: translate('dashboard-details-component.saved-views-unsaved-dialog.content', {
            name: activeView.name,
          }),
          cancelTitle: translate('simple-dialog.cancel'),
          confirmTitle: translate('dashboard-details-component.saved-views-unsaved-dialog.apply'),
          confirmColor: SimpleDialogButtonColor.blue,
          buttons: [
            {
              title: translate('dashboard-details-component.saved-views-unsaved-dialog.discard'),
              color: SimpleDialogButtonColor.orange,
              handler: (dialogRef) => {
                dialogRef.close('discard');
              },
            },
          ],
        })
        .afterClosed()
        .subscribe(async (result) => {
          if (result === 'discard') {
            if (this.dashboardViewComponent && this.activeSavedView) {
              this.dashboardViewComponent.applySavedView(this.activeSavedView);
              this.activeSavedView.hasUnsavedChanges = false;
            }

            this.onSavedViewBannerSaveAs();
            return;
          }

          if (result === true) {
            const saved = await this.onSavedViewBannerSave();
            if (saved) {
              this.onSavedViewBannerSaveAs();
            }
          }
        });

      return;
    }

    this.onSavedViewBannerSaveAs();
  }

  onSavedViewAction($event: { itemType: string; action: string; item: SavedView }): void {
    switch ($event.action) {
      case 'load':
        this.onLoadSavedViewRequested($event.item);
        break;
      case 'delete':
        this.deleteSavedView($event.item);
        break;
      case 'rename':
        this.renameSavedView($event.item);
        break;
      case 'close':
        if (this.dashboardViewComponent) {
          if (this.isReadOnly) {
            this.dashboardViewComponent.revertDashboardAction();
          } else if (this.isDashboardUnsaved) {
            this.dashboardViewComponent.revertDashboard();
          }
        }

        this.closeSavedView($event.item);
        break;
    }
  }

  private loadSavedView(savedView: SavedView): void {
    this.savedViews = this.savedViews.map((view) => ({
      ...view,
      isActive: view.id === savedView.id,
    }));

    this.activeSavedViewId = savedView.id;
    this.recomputeActiveSavedViewCache();

    this.savedViewService
      .getSavedViewById(this.projectId, this.dashboardSavedViewsDashboardId, savedView.id)
      .then(async (backendView: SavedViewBackendModel) => {
        const isStillActive = this.activeSavedViewId === backendView.id;
        const hasTargetView = this.savedViews.some((view) => view.id === backendView.id);
        if (!hasTargetView) {
          return;
        }

        let loadedView: SavedView = {
          id: backendView.id,
          name: backendView.viewName,
          updatedAt: backendView.modified_at_date,
          widgets: backendView.widgets,
          widgetGroups: backendView.widget_groups,
          dashboardRevision: backendView.dashboard_revision,
          isActive: true,
          hasUnsavedChanges: false,
        };

        // Check if propagation is needed based on dashboard revision
        const dashboard = this.dashboardViewComponent?.dashboard;
        if (
          dashboard &&
          this.savedViewPropagationService.needsPropagation(
            dashboard.revision,
            backendView.dashboard_revision
          )
        ) {
          const propagationResult = this.savedViewPropagationService.analyzeChanges(
            dashboard,
            loadedView
          );

          if (propagationResult.hasChanges) {
            loadedView = this.savedViewPropagationService.applyPropagation(
              dashboard,
              loadedView,
              propagationResult
            );
            loadedView.wasPropagated = true;

            // Persist the propagated changes to backend
            try {
              await this.savedViewService.updateSavedView(
                this.projectId,
                this.dashboardSavedViewsDashboardId,
                loadedView.id,
                {
                  dashboardRevision: dashboard.revision,
                  viewName: loadedView.name,
                  widgets: loadedView.widgets,
                  widgetGroups: loadedView.widgetGroups,
                }
              );
            } catch (error) {
              this.logger.error('Failed to persist propagated saved view changes', { error });
            }
          }
        }

        this.savedViews = this.savedViews.map((view) =>
          view.id === loadedView.id
            ? {
                ...view,
                ...loadedView,
              }
            : view
        );
        this.recomputeActiveSavedViewCache();

        if (isStillActive && this.isReadOnly && this.dashboardViewComponent) {
          const updatedView = this.activeSavedView;
          if (updatedView) {
            this.dashboardViewComponent.applySavedView(updatedView);

            // Show notification if changes were propagated
            if (loadedView.wasPropagated) {
              this.alertService.showToast(
                translate('dashboard-details-component.saved-views.toast-propagated')
              );
            }
          }
        }
      })
      .catch((error) => {
        this.alertService.showErrorToast(
          translate('dashboard-details-component.saved-views.error-load-view'),
          error
        );
      });
  }

  private deleteSavedView(savedView: SavedView): void {
    if (!this.projectId || !this.dashboardSavedViewsDashboardId) {
      return;
    }

    this.dialogService
      .showWarningPopup({
        title: translate('dashboard-details-component.delete-saved-view-title'),
        content: translate('dashboard-details-component.delete-saved-view-contents', {
          name: savedView.name,
        }),
        confirmTitle: translate('dashboard-details-component.delete-saved-view-confirm'),
        cancelTitle: translate('dashboard-details-component.delete-saved-view-cancel'),
        confirmColor: SimpleDialogButtonColor.red,
      })
      .afterClosed()
      .subscribe((result) => {
        if (!result) {
          return;
        }

        this.savedViewService
          .deleteSavedView(this.projectId, this.dashboardSavedViewsDashboardId, savedView.id)
          .then(() => {
            const isDeletingActiveView = this.activeSavedViewId === savedView.id;

            if (isDeletingActiveView && this.dashboardViewComponent) {
              if (this.isReadOnly) {
                this.dashboardViewComponent.revertDashboardAction();
              } else if (this.isDashboardUnsaved) {
                this.dashboardViewComponent.revertDashboard();
              }
            }

            this.savedViews = this.savedViews.filter((view) => view.id !== savedView.id);

            if (isDeletingActiveView) {
              this.activeSavedViewId = undefined;
              this.recomputeActiveSavedViewCache();
            }

            this.alertService.showToast(
              translate('dashboard-details-component.saved-views.toast-deleted', {
                name: savedView.name,
              })
            );
          })
          .catch((error) => {
            this.alertService.showErrorToast(
              translate('dashboard-details-component.saved-views.error-delete-view'),
              error
            );
          });
      });
  }

  private renameSavedView(savedView: SavedView): void {
    const existingNames: string[] = this.savedViews
      .filter((view) => view.id !== savedView.id)
      .map((view) => view.name);

    this.savedViewDialogMode = 'rename';
    this.savedViewToRename = savedView;
    this.savedViewDialogExistingNames = existingNames;
    this.savedViewDialogInitialName = savedView.name;
    this.savedViewDialogTitle = translate('dashboard-details-component.saved-views.rename-title');
    this.isSavedViewDialogOpen = true;
  }

  private closeSavedView(savedView: SavedView): void {
    if (this.activeSavedViewId !== savedView.id) {
      return;
    }

    this.savedViews = this.savedViews.map((view) => ({
      ...view,
      isActive: false,
    }));

    this.activeSavedViewId = undefined;
    this.recomputeActiveSavedViewCache();
  }

  onDashboardUnsavedChanged(isUnsaved: boolean): void {
    if (!this.activeSavedViewId) {
      // No Saved View loaded: this refers to dashboard-level unsaved state
      this.isDashboardUnsaved = isUnsaved;
      return;
    }

    const activeView = this.savedViews.find((view) => view.id === this.activeSavedViewId);
    if (!activeView) {
      return;
    }

    activeView.hasUnsavedChanges = isUnsaved;
  }

  async onSavedViewBannerSave(): Promise<boolean> {
    if (!this.activeSavedViewId) {
      return false;
    }

    const activeView = this.activeSavedView;
    if (!activeView) {
      return false;
    }

    try {
      const snapshot = await this.getDashboardSnapshotForSavedView();
      if (!snapshot) {
        return false;
      }

      const backendView = await this.savedViewService.updateSavedView(
        this.projectId,
        this.dashboardSavedViewsDashboardId,
        activeView.id,
        {
          dashboardRevision: snapshot.revision,
          viewName: activeView.name,
          widgets: snapshot.widgets,
          widgetGroups: snapshot.widgetGroups,
        }
      );

      this.savedViews = this.savedViews.map((view) =>
        view.id === backendView.id
          ? {
              ...view,
              name: backendView.viewName,
              updatedAt: backendView.modified_at_date,
              hasUnsavedChanges: false,
              widgets: backendView.widgets,
              widgetGroups: backendView.widget_groups,
            }
          : view
      );

      this.recomputeActiveSavedViewCache();

      if (this.isReadOnly && this.dashboardViewComponent) {
        const updatedView = this.activeSavedView;
        if (updatedView) {
          this.dashboardViewComponent.setSavedViewBaseline(updatedView);
        }
      }

      this.alertService.showToast(
        translate('dashboard-details-component.saved-views.toast-saved-as', {
          name: backendView.viewName,
        })
      );

      return true;
    } catch (error) {
      this.alertService.showErrorToast(
        translate('dashboard-details-component.saved-views.error-save-view'),
        error
      );
      return false;
    }
  }

  private onLoadSavedViewRequested(targetView: SavedView): void {
    if (!targetView) {
      return;
    }

    const activeView = this.activeSavedView;
    const isSwitchingToDifferentView =
      !!this.activeSavedViewId && this.activeSavedViewId !== targetView.id;

    if (!isSwitchingToDifferentView || !activeView?.hasUnsavedChanges) {
      this.loadSavedView(targetView);
      return;
    }

    this.dialogService
      .showSimpleDialog({
        title: translate('dashboard-details-component.saved-views-unsaved-dialog.title'),
        content: translate('dashboard-details-component.saved-views-unsaved-dialog.content', {
          name: activeView.name,
        }),
        cancelTitle: translate('simple-dialog.cancel'),
        confirmTitle: translate('dashboard-details-component.saved-views-unsaved-dialog.apply'),
        confirmColor: SimpleDialogButtonColor.blue,
        buttons: [
          {
            title: translate('dashboard-details-component.saved-views-unsaved-dialog.discard'),
            color: SimpleDialogButtonColor.orange,
            handler: (dialogRef) => {
              dialogRef.close('discard');
            },
          },
        ],
      })
      .afterClosed()
      .subscribe(async (result) => {
        if (result === 'discard') {
          if (this.dashboardViewComponent && this.activeSavedView) {
            this.dashboardViewComponent.applySavedView(this.activeSavedView);
          }

          this.savedViews = this.savedViews.map((view) =>
            view.id === this.activeSavedViewId ? { ...view, hasUnsavedChanges: false } : view
          );
          this.recomputeActiveSavedViewCache();

          this.loadSavedView(targetView);
          return;
        }

        if (result === true) {
          const saved = await this.onSavedViewBannerSave();
          if (saved) {
            this.loadSavedView(targetView);
          }
        }
      });
  }

  async onSavedViewBannerSaveAs(): Promise<void> {
    if (!this.projectId) {
      return;
    }

    this.savedViewDialogMode = 'create';
    this.savedViewToRename = undefined;
    this.savedViewDialogExistingNames = this.savedViews.map((view) => view.name);
    this.savedViewDialogInitialName = '';
    this.savedViewDialogTitle = undefined;
    this.isSavedViewDialogOpen = true;
  }

  onSavedViewBannerRevert(): void {
    if (!this.dashboardViewComponent) {
      return;
    }
    if (this.isReadOnly) {
      if (!this.activeSavedViewId) {
        return;
      }

      const activeView = this.activeSavedView;
      if (!activeView) {
        return;
      }

      // For read-only users, Revert should discard changes made since loading/saving
      // the Saved View and restore the Saved View snapshot itself.
      this.dashboardViewComponent.applySavedView(activeView);
      activeView.hasUnsavedChanges = false;
      return;
    }

    // For editable dashboards (if Saved Views are ever enabled), keep existing behavior.
    this.dashboardViewComponent.revertDashboard();
  }

  onSavedViewBannerClose(): void {
    if (!this.activeSavedViewId) {
      return;
    }

    const activeView = this.activeSavedView;
    if (!activeView) {
      return;
    }

    if (this.dashboardViewComponent) {
      if (this.isReadOnly) {
        // For read-only users, closing a saved view should restore the original dashboard
        this.dashboardViewComponent.revertDashboardAction();
      } else if (this.isDashboardUnsaved) {
        // For editable dashboards, keep existing behavior (prompted revert of unsaved changes)
        this.dashboardViewComponent.revertDashboard();
      }
    }

    this.closeSavedView(activeView);
  }

  async onSavedViewDialogClose(name: string | null): Promise<void> {
    if (!this.isSavedViewDialogOpen) {
      return;
    }

    this.isSavedViewDialogOpen = false;

    if (!name) {
      this.resetSavedViewDialogState();
      return;
    }

    if (this.savedViewDialogMode === 'create') {
      await this.createSavedView(name);
    } else if (this.savedViewDialogMode === 'rename' && this.savedViewToRename) {
      await this.commitSavedViewRename(this.savedViewToRename, name);
    }

    this.resetSavedViewDialogState();
  }

  private async createSavedView(name: string): Promise<void> {
    try {
      const snapshot = await this.getDashboardSnapshotForSavedView();
      if (!snapshot) {
        return;
      }

      this.savedViewService
        .createSavedView(this.projectId, snapshot.id, {
          dashboardRevision: snapshot.revision,
          viewName: name,
          widgets: snapshot.widgets,
          widgetGroups: snapshot.widgetGroups,
        })
        .then((created: SavedViewBackendModel) => {
          const newView: SavedView = {
            id: created.id,
            name: created.viewName,
            updatedAt: created.modified_at_date,
            isActive: true,
            hasUnsavedChanges: false,
            widgets: created.widgets,
            widgetGroups: created.widget_groups,
          };

          this.savedViews = [
            ...this.savedViews.map((view) => ({
              ...view,
              isActive: false,
              hasUnsavedChanges: false,
            })),
            newView,
          ];
          this.activeSavedViewId = newView.id;
          this.recomputeActiveSavedViewCache();

          this.alertService.showToast(
            translate('dashboard-details-component.saved-views.toast-saved-as', {
              name,
            })
          );

          if (this.isReadOnly && this.dashboardViewComponent) {
            this.dashboardViewComponent.setSavedViewBaseline(newView);
          }
        })
        .catch((error) => {
          this.alertService.showErrorToast(
            translate('dashboard-details-component.saved-views.error-save-view'),
            error
          );
        });
    } catch (error) {
      this.alertService.showErrorToast(
        translate('dashboard-details-component.saved-views.error-save-view'),
        error
      );
    }
  }

  private async commitSavedViewRename(savedView: SavedView, name: string): Promise<void> {
    this.savedViewService
      .updateSavedView(this.projectId, this.dashboardSavedViewsDashboardId, savedView.id, {
        viewName: name,
      })
      .then((backendView: SavedViewBackendModel) => {
        this.savedViews = this.savedViews.map((view) =>
          view.id === backendView.id
            ? {
                ...view,
                name: backendView.viewName,
                updatedAt: backendView.modified_at_date,
              }
            : view
        );
        this.recomputeActiveSavedViewCache();

        this.alertService.showToast(
          translate('dashboard-details-component.saved-views.toast-renamed', {
            name: backendView.viewName,
          })
        );
      })
      .catch((error) => {
        this.alertService.showErrorToast(
          translate('dashboard-details-component.saved-views.error-rename-view'),
          error
        );
      });
  }

  private resetSavedViewDialogState(): void {
    this.savedViewDialogMode = 'create';
    this.savedViewToRename = undefined;
    this.savedViewDialogExistingNames = [];
    this.savedViewDialogInitialName = undefined;
    this.savedViewDialogTitle = undefined;
  }

  private recomputeActiveSavedViewCache(): void {
    if (!this.activeSavedViewId) {
      this.activeSavedViewCache = undefined;
      return;
    }

    this.activeSavedViewCache = this.savedViews.find((view) => view.id === this.activeSavedViewId);
  }

  private onActivityBarDataSourceAction(
    event: ActivityBarInteraction<WidgetDataSource | EntityChange<WidgetDataSource>>
  ) {
    if (event.action === 'changeDataSource') {
      this.replaceExistingDataSourceWithNewOne(
        event.item as EntityChange<WidgetDataSource>,
        event.widget
      );
    }
  }

  private replaceExistingDataSourceWithNewOne(
    dsChange: EntityChange<WidgetDataSource>,
    widget?: Widget
  ) {
    if (widget) {
      const widgetComponent = this.dashboardViewComponent.elementFor(widget.id);
      if (!widgetComponent) {
        return;
      }
      widgetComponent.changeDataSource(dsChange.new);
      return;
    }
    const oldDsWidgets = this.dashboardViewComponent.currentPageWidgets.filter(
      (w) =>
        (w.dataSourceId === dsChange.old.id || !w.dataSourceId) &&
        w.payload.type !== 'note' &&
        w.payload.type !== 'noteMarkdown' &&
        w.payload.type !== 'insightFeed'
    );
    if (oldDsWidgets.length < 1) {
      return;
    }
    oldDsWidgets.forEach((w) => {
      const widgetComponent = this.dashboardViewComponent.elementFor(w.id);
      if (!widgetComponent) {
        return;
      }
      widgetComponent.changeDataSource(dsChange.new);
    });
  }

  private handleComparisonChange(
    comparisonChange: EntityChange<Comparison>,
    widget?: Widget
  ): void {
    if (widget) {
      this.comparisonService.applyComparison(comparisonChange.new, widget, true);
      return;
    }
    comparisonChange.old.widgets.forEach((w) => {
      const widgetToHandle = this.dashboardViewComponent.currentPageWidgets.find(
        (wgt) => wgt.id === w
      );
      if (widgetToHandle) {
        if (comparisonChange.new) {
          this.comparisonService.applyComparison(comparisonChange.new, widgetToHandle, true);
        } else {
          this.comparisonService.revokeComparison(comparisonChange.old, widgetToHandle);
        }
      }
    });
  }

  private onActivityBarComparisonAction(
    event: ActivityBarInteraction<Comparison | EntityChange<Comparison>>
  ) {
    switch (event.action) {
      case 'changeComparison':
        this.handleComparisonChange(event.item as EntityChange<Comparison>, event?.widget);
        break;
      case 'edit':
        this.onComparisonAction(event, { isActivityBarSelectAction: true });
        break;
      case 'apply':
        this.onComparisonAction(event, { isActivityBarSelectAction: true });
        break;
      case 'remove':
        const widgetComponent = this.dashboardViewComponent.elementFor(event.widget.id);
        if (widgetComponent) {
          widgetComponent.onRemoveFromComparison();
        }
        break;
    }
  }

  public onActivitybarInteraction(
    event: ActivityBarInteraction<
      WidgetGroup | WidgetDataSource | EntityChange<WidgetDataSource | Comparison> | Comparison
    >
  ): void {
    switch (event.type) {
      case ActivityBarPillTypeEnum.WIDGET_GROUP:
        this.onWidgetGroupAction(event, { isActivityBarSelectAction: true });
        break;
      case ActivityBarPillTypeEnum.DATA_SOURCE:
        this.onActivityBarDataSourceAction(
          event as ActivityBarInteraction<WidgetDataSource | EntityChange<WidgetDataSource>>
        );
        break;
      case ActivityBarPillTypeEnum.COMPARISON:
        this.onActivityBarComparisonAction(event as ActivityBarInteraction<Comparison>);
        break;
    }
  }

  onDashboardParametersChanged($event) {
    this.project = $event.project;
    this.projectId = $event.project.id;
    this.dashboardSavedViewsDashboardId = $event.dashboardId;

    if (!this.projectId || !this.dashboardSavedViewsDashboardId) {
      return;
    }

    if (!this.isReadOnly) {
      return;
    }

    this.savedViewService
      .getSavedViews(this.projectId, this.dashboardSavedViewsDashboardId)
      .then((response) => {
        const hits: SavedViewBackendModel[] = response?.result?.hits ?? [];
        this.savedViews = hits.map((backendView) => ({
          id: backendView.id,
          name: backendView.viewName,
          updatedAt: backendView.modified_at_date,
          isActive: this.activeSavedViewId === backendView.id,
          hasUnsavedChanges: false,
          widgets: backendView.widgets,
          widgetGroups: backendView.widget_groups,
          dashboardRevision: backendView.dashboard_revision,
        }));
      })
      .catch((error) => {
        const status = error?.status;
        if (status === 403 || status === 404) {
          this.logger.warn('Saved views are not available for this dashboard', { error });
          return;
        }

        this.alertService.showErrorToast(
          translate('dashboard-details-component.saved-views.error-load-views'),
          error
        );
      });
  }
}
